import { apiClient } from '@/shared/services/api/client';

/**
 * 多项目检测作业 API 客户端
 *
 * 对接后端：modules/detection_agent/api/routers/multi_project_tasks.py
 *
 * 端点：
 * - POST /api/v1/detection-tasks/multi-project-jobs            创建作业（202 + job_id）
 * - GET  /api/v1/detection-tasks/multi-project-jobs/{job_id}  查询进度
 * - POST /api/v1/detection-tasks/multi-project-jobs/{job_id}/cancel  取消作业
 */

export interface MultiProjectJobCreateRequest {
  project_ids: string[];
  strategy_id?: string;
  rule_ids?: string[];
  model_id?: string;
  detection_scope?: 'full' | 'incremental';
  task_group?: string;
  /** 兼容输入：单值目标 ID；多机请用 ssh_target_ids */
  ssh_target_id?: string;
  /** 兼容输入：单条内联配置；多机请用 ssh_configs */
  ssh_config?: string;
  /** 多机：SSH 目标 ID 列表（重复表单字段） */
  ssh_target_ids?: string[];
  /** 多机：内联配置 JSON 列表（重复表单字段） */
  ssh_configs?: string[];
  serial_ssh?: boolean;
}

export interface MultiProjectJobStatus {
  /** 后端来源：modules/detection_agent/api/schemas/multi_project_schemas.py:MultiProjectJobStatus */
  job_id: string;
  status: 'pending' | 'running' | 'completed' | 'failed' | 'cancelled';
  user_id: number;
  project_ids: string[];
  projects_total: number;
  projects_processed: number;
  files_total: number;
  files_processed: number;
  tasks_created: number;
  skipped_projects: Array<{ project_id: string; reason: string }>;
  error_message?: string;
  progress_message?: string;
  started_at?: string;
  completed_at?: string;
  created_at?: string;
}

export interface MultiProjectJobCreateResponse {
  job_id: string;
  status: 'pending' | 'running' | 'completed' | 'failed' | 'cancelled';
  created_at: string;
}

export interface MultiProjectJobCancelResponse {
  job_id: string;
  status: 'cancelled';
  message: string;
}

const BASE_PATH = '/api/v1/detection-tasks';

export const multiProjectTaskApi = {
  /**
   * 创建多项目检测作业。
   * 后端立即返回 202 + job_id，异步执行；前端通过 getJob 轮询进度。
   */
  async createJob(
    req: MultiProjectJobCreateRequest,
    signal?: AbortSignal,
  ): Promise<{ data: MultiProjectJobCreateResponse }> {
    const formData = new FormData();
    req.project_ids.forEach(pid => formData.append('project_ids', pid));
    if (req.strategy_id) formData.append('strategy_id', req.strategy_id);
    if (req.rule_ids && req.rule_ids.length > 0) {
      req.rule_ids.forEach(r => formData.append('rule_ids', r));
    }
    if (req.model_id) formData.append('model_id', req.model_id);
    if (req.detection_scope) {
      formData.append('detection_scope', req.detection_scope);
    } else {
      formData.append('detection_scope', 'full'); // 多项目强制 full
    }
    if (req.task_group) formData.append('task_group', req.task_group);
    if (req.ssh_target_id) formData.append('ssh_target_id', req.ssh_target_id);
    if (req.ssh_config) formData.append('ssh_config', req.ssh_config);
    // 多机：与 rule_ids 同一套重复字段写法
    if (req.ssh_target_ids && req.ssh_target_ids.length > 0) {
      req.ssh_target_ids.forEach(id => formData.append('ssh_target_ids', id));
    }
    if (req.ssh_configs && req.ssh_configs.length > 0) {
      req.ssh_configs.forEach(cfg => formData.append('ssh_configs', cfg));
    }
    if (req.serial_ssh !== undefined) {
      formData.append('serial_ssh', req.serial_ssh ? 'true' : 'false');
    }

    const response = await apiClient.post(`${BASE_PATH}/multi-project-jobs`, formData, {
      signal,
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  /**
   * 查询多项目检测作业进度。
   * 轮询策略：1s 间隔，超时按 projects_total 动态计算（见 useTaskCreationFlow.ts）。
   */
  async getJob(
    jobId: string,
    signal?: AbortSignal,
  ): Promise<{ data: MultiProjectJobStatus }> {
    const response = await apiClient.get(`${BASE_PATH}/multi-project-jobs/${jobId}`, { signal });
    return response.data;
  },

  /**
   * 取消多项目检测作业（best-effort，已派发的批不可回滚）。
   */
  async cancelJob(
    jobId: string,
    signal?: AbortSignal,
  ): Promise<{ data: MultiProjectJobCancelResponse }> {
    const response = await apiClient.post(
      `${BASE_PATH}/multi-project-jobs/${jobId}/cancel`,
      undefined,
      { signal },
    );
    return response.data;
  },
};
